import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { Box, Stack } from '@chakra-ui/react';
import { map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import {
  renderRiskContext,
  renderTableHeaderWithLoader,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

export const ResourcesByResourceGroup: FC = () => {
  const {
    resourceUuid,
    resourceAccountId,
    resourceGroup,
    resourceType,
  } = useSelector(selectActiveResource);
  const dispatch = useDispatch();
  const { resources, riskContext, alertCount } = resourceGroup;
  const { renderStackedAccountNameID } = useCloudAccountId();
  const { environmentId } = useAccessBoundary();

  useEffect(() => {
    const onSuccess = res => {
      if (res.items.length === 0) return;
      dispatch(
        actions.getRiskContextForResourcesByResourceGroup({
          q: { riskContextRequest: { uuids: res?.items.map(r => r.uuid) } },
        }),
      );
      dispatch(
        actions.getAlertCount({
          q: {
            environmentId: environmentId!,
            accountIds: [resourceAccountId],
            resourceUuids: map(res?.items, o => o?.uuid),
          },
        }),
      );
    };
    if (resourceType === NativeResources.AzureResourceGroup)
      dispatch(
        actions.getResourcesByResourceGroup({
          q: { resourceGroupUuid: resourceUuid, accountId: resourceAccountId },
          onSuccess,
        }),
      );
    else
      dispatch(
        actions.getDataSourcesByStorageAccount({
          q: { storageAccountUuid: resourceUuid, accountId: resourceAccountId },
          onSuccess,
        }),
      );
  }, [resourceAccountId, resourceUuid, dispatch, resourceType, environmentId]);

  const columns: TableColumnProps[] = [
    {
      header: 'Name',
      render: ({ row }) => (
        <WithResourceIcon resourceType={row.resource_type}>
          <StackedCell upper={row.name} lower={row.resource_id} />
        </WithResourceIcon>
      ),
    },
    { header: 'Type', accessor: 'native_name', align: 'left' },
    {
      header: 'Account',
      accessor: 'type',
      align: 'left',
      render: ({ row }) => renderStackedAccountNameID(row?.account_id),
    },
    {
      header: 'Deployed In',
      accessor: 'type',
      align: 'left',
      render: ({ row }) => <StackedCell upper={row?.region} lower={row?.vpc} />,
    },
    {
      header: renderTableHeaderWithLoader('Alerts', alertCount.isLoading),
      align: 'left',
      render: ({ row }) => alertCount.data?.[row?.uuid] ?? 0,
    },
    {
      header: renderTableHeaderWithLoader(
        'Risk Context',
        riskContext.isLoading,
      ),
      accessor: 'type',
      align: 'left',
      render: ({ row }) => renderRiskContext(riskContext.data?.[row?.uuid]),
    },
  ];

  return (
    <Stack w={'full'} h={'full'} pb={5} spacing={5}>
      <Box h="full">
        <Table
          columns={columns}
          data={resourceGroup.resources.data}
          isLoading={resourceGroup.resources.isLoading}
          pagination={{
            totalCount: resources.page.totalCount,
            pageInfo: resources.page.info,
            onChange: pageInfo => {
              dispatch(
                actions.updateResourceGroupResourcesPagination(pageInfo),
              );
            },
          }}
        />
      </Box>
    </Stack>
  );
};
